<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-2xl mx-auto w-full px-4 py-8">
      <div v-if="loading" class="flex justify-center py-20">
        <div class="w-10 h-10 border-4 border-[#b99a52] border-t-transparent rounded-full animate-spin"></div>
      </div>

      <template v-else-if="appointment">
        <!-- Breadcrumb -->
        <router-link :to="{ name: isProvider ? 'provider-schedule' : 'appointment-list' }" class="text-sm text-[#b99a52] hover:underline flex items-center gap-1 mb-4">
          <span class="mdi mdi-arrow-left"></span> Meus Agendamentos
        </router-link>

        <!-- Status badge -->
        <div class="flex items-center justify-between mb-5">
          <h1 class="text-2xl font-bold text-[#152a34]">Agendamento #{{ appointment.id }}</h1>
          <span :class="['px-3 py-1 rounded-full text-xs font-semibold', statusClass]">{{ statusLabel }}</span>
        </div>

        <!-- Stepper -->
        <div class="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
          <div
            v-for="(step, i) in steps"
            :key="step.key"
            class="flex items-center gap-1 flex-shrink-0"
          >
            <div :class="[
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium',
              currentStepIndex >= i
                ? 'bg-[#152a34] text-white'
                : 'bg-gray-100 text-gray-400'
            ]">
              <span class="mdi" :class="step.icon"></span>
              {{ step.label }}
            </div>
            <span v-if="i < steps.length - 1" class="mdi mdi-chevron-right text-gray-300 flex-shrink-0"></span>
          </div>
        </div>

        <!-- Dados do serviço -->
        <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-5">
          <h2 class="font-semibold text-[#152a34] mb-4">Detalhes do Serviço</h2>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Serviço</span>
              <span class="font-medium">{{ appointment.service?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Prestador</span>
              <span class="font-medium">{{ appointment.provider?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Contratante</span>
              <span class="font-medium">{{ appointment.contractor?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Data e Hora</span>
              <span class="font-medium">{{ scheduledAtDisplay }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Duração</span>
              <span class="font-medium">{{ appointment.duration_minutes }} min</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Valor</span>
              <span class="font-bold text-[#152a34]">R$ {{ formatPrice(appointment.service?.price_with_fee) }}</span>
            </div>
          </div>
        </div>

        <!-- Endereço -->
        <div v-if="contractorAddress" class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-5">
          <div class="flex items-center justify-between mb-3">
            <h2 class="font-semibold text-[#152a34]">Endereço do Serviço</h2>
            <button
              @click="copyAddress"
              :title="addressCopied ? 'Copiado!' : 'Copiar endereço'"
              class="flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition"
              :class="addressCopied ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-[#b99a52] hover:bg-gray-50'"
            >
              <span class="mdi text-base" :class="addressCopied ? 'mdi-check' : 'mdi-content-copy'"></span>
              {{ addressCopied ? 'Copiado!' : 'Copiar' }}
            </button>
          </div>
          <p class="text-sm text-gray-600">
            {{ contractorAddress.address }}, {{ contractorAddress.number }}
            <span v-if="contractorAddress.complement"> — {{ contractorAddress.complement }}</span><br>
            {{ contractorAddress.neighborhood }}, {{ contractorAddress.city }}/{{ contractorAddress.state }}
            <span v-if="contractorAddress.zip_code"> — {{ contractorAddress.zip_code }}</span>
          </p>
        </div>

        <!-- Observações -->
        <div v-if="appointment.notes" class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-5">
          <h2 class="font-semibold text-[#152a34] mb-2">Observações</h2>
          <p class="text-sm text-gray-600">{{ appointment.notes }}</p>
        </div>

        <!-- Ações -->
        <div class="space-y-3">
          <!-- Prestador: confirmar agendamento -->
          <button
            v-if="isProvider && appointment.status === 'pending'"
            @click="showConfirmModal = true"
            :disabled="actionLoading"
            class="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-50"
          >
            Confirmar Agendamento
          </button>

          <!-- Prestador: marcar como concluído -->
          <button
            v-if="isProvider && appointment.status === 'confirmed'"
            @click="showCompleteModal = true"
            :disabled="actionLoading"
            class="w-full py-3 bg-[#152a34] text-white rounded-xl font-semibold hover:bg-[#1e3a4a] transition disabled:opacity-50"
          >
            Marcar como Concluído
          </button>

          <!-- Contratante: confirmar conclusão -->
          <button
            v-if="!isProvider && appointment.status === 'in_progress'"
            @click="showCompleteModal = true"
            :disabled="actionLoading"
            class="w-full py-3 bg-[#152a34] text-white rounded-xl font-semibold hover:bg-[#1e3a4a] transition disabled:opacity-50"
          >
            Confirmar Conclusão do Serviço
          </button>

          <!-- Contratante: pagar -->
          <router-link
            v-if="!isProvider && appointment.status === 'awaiting_payment'"
            :to="{ name: 'appointment-payment', params: { id: appointment.id } }"
            class="block w-full py-3 bg-[#b99a52] text-white rounded-xl font-semibold hover:bg-[#a08840] transition text-center"
          >
            Realizar Pagamento
          </router-link>

          <!-- Avaliar (após completed) -->
          <div
            v-if="appointment.status === 'completed' && canReview"
            class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5"
          >
            <h3 class="font-semibold text-[#152a34] mb-3">Avalie o {{ isProvider ? 'Contratante' : 'Prestador' }}</h3>
            <div class="flex gap-2 mb-3">
              <button
                v-for="star in 5"
                :key="star"
                @click="reviewRating = star"
                class="text-2xl transition"
                :class="star <= reviewRating ? 'text-yellow-400' : 'text-gray-200'"
              >★</button>
            </div>
            <textarea
              v-model="reviewComment"
              rows="2"
              placeholder="Deixe um comentário..."
              class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#b99a52] mb-3"
            ></textarea>
            <button
              @click="submitReview"
              :disabled="reviewRating === 0 || reviewSubmitting"
              class="w-full py-2 bg-[#152a34] text-white rounded-xl font-semibold hover:bg-[#1e3a4a] transition disabled:opacity-50"
            >
              <span v-if="reviewSubmitting" class="mdi mdi-loading animate-spin mr-1"></span>
              Enviar Avaliação
            </button>
          </div>

          <!-- Cancelar -->
          <button
            v-if="['pending', 'confirmed'].includes(appointment.status)"
            @click="showCancelModal = true"
            class="w-full py-2 border border-red-300 text-red-500 rounded-xl text-sm hover:bg-red-50 transition"
          >
            Cancelar Agendamento
          </button>
        </div>

        <!-- Modal confirmação de agendamento -->
        <div v-if="showConfirmModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div class="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <div class="flex items-center gap-3 mb-4">
              <span class="mdi mdi-calendar-check text-3xl text-green-600"></span>
              <h3 class="font-bold text-[#152a34] text-lg">Confirmar Agendamento</h3>
            </div>

            <div class="bg-gray-50 rounded-xl p-4 mb-4 space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-500">Serviço</span>
                <span class="font-medium text-[#152a34]">{{ appointment.service?.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Contratante</span>
                <span class="font-medium text-[#152a34]">{{ appointment.contractor?.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Data e Hora</span>
                <span class="font-medium text-[#152a34]">{{ scheduledAtDisplay }}</span>
              </div>
            </div>

            <div class="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-5 text-sm text-blue-700">
              <span class="mdi mdi-email-outline text-lg flex-shrink-0 mt-0.5"></span>
              <p>Ao confirmar, um e-mail será enviado automaticamente para <strong>{{ appointment.contractor?.name }}</strong> informando que o agendamento foi aceito.</p>
            </div>

            <div class="flex gap-3">
              <button
                @click="showConfirmModal = false"
                class="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm hover:bg-gray-50 transition"
              >Voltar</button>
              <button
                @click="showConfirmModal = false; action('confirm')"
                :disabled="actionLoading"
                class="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition disabled:opacity-50"
              >
                <span v-if="actionLoading" class="mdi mdi-loading animate-spin mr-1"></span>
                Confirmar
              </button>
            </div>
          </div>
        </div>

        <!-- Modal conclusão -->
        <div v-if="showCompleteModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div class="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <div class="flex items-center gap-3 mb-4">
              <span class="mdi mdi-check-circle text-3xl text-[#152a34]"></span>
              <h3 class="font-bold text-[#152a34] text-lg">
                {{ isProvider ? 'Marcar como Concluído' : 'Confirmar Conclusão' }}
              </h3>
            </div>

            <div class="bg-gray-50 rounded-xl p-4 mb-4 space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-500">Serviço</span>
                <span class="font-medium text-[#152a34]">{{ appointment.service?.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">{{ isProvider ? 'Contratante' : 'Prestador' }}</span>
                <span class="font-medium text-[#152a34]">{{ isProvider ? appointment.contractor?.name : appointment.provider?.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Data e Hora</span>
                <span class="font-medium text-[#152a34]">{{ scheduledAtDisplay }}</span>
              </div>
            </div>

            <div class="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5 text-sm text-amber-700">
              <span class="mdi mdi-information-outline text-lg flex-shrink-0 mt-0.5"></span>
              <p v-if="isProvider">
                Ao confirmar, o serviço será marcado como realizado e um e-mail será enviado para <strong>{{ appointment.contractor?.name }}</strong> solicitando a confirmação e o pagamento.
              </p>
              <p v-else>
                Ao confirmar, você está declarando que o serviço foi prestado. O agendamento seguirá para a etapa de pagamento.
              </p>
            </div>

            <div class="flex gap-3">
              <button
                @click="showCompleteModal = false"
                class="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm hover:bg-gray-50 transition"
              >Voltar</button>
              <button
                @click="showCompleteModal = false; action('complete')"
                :disabled="actionLoading"
                class="flex-1 py-2.5 bg-[#152a34] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a4a] transition disabled:opacity-50"
              >
                <span v-if="actionLoading" class="mdi mdi-loading animate-spin mr-1"></span>
                Confirmar
              </button>
            </div>
          </div>
        </div>

        <!-- Modal cancelamento -->
        <div v-if="showCancelModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div class="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 class="font-bold text-[#152a34] mb-3">Cancelar Agendamento</h3>
            <textarea
              v-model="cancelReason"
              rows="3"
              placeholder="Motivo do cancelamento (mínimo 3 caracteres)..."
              class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none mb-4 focus:outline-none focus:ring-2 focus:ring-red-300"
            ></textarea>
            <p
              v-if="cancelReasonTouched && !isCancelReasonValid"
              class="text-xs text-red-600 mb-3"
            >
              Informe o motivo do cancelamento com no mínimo 3 caracteres.
            </p>
            <div class="flex gap-3">
              <button @click="showCancelModal = false" class="flex-1 py-2 border border-gray-200 rounded-xl text-sm hover:bg-gray-50">Voltar</button>
              <button @click="action('cancel')" :disabled="actionLoading || !isCancelReasonValid" class="flex-1 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-50">
                Confirmar
              </button>
            </div>
          </div>
        </div>

        <!-- Modal feedback ação -->
        <div
          v-if="showFeedbackModal"
          class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div class="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full text-center">
            <h3 class="font-bold text-[#152a34] mb-3">
              {{ feedbackTitle }}
            </h3>
            <p class="text-sm text-gray-600 mb-5">
              {{ feedbackMessage }}
            </p>
            <button
              @click="showFeedbackModal = false"
              class="w-full py-2 bg-[#152a34] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a4a] transition"
            >
              Ok
            </button>
          </div>
        </div>

        <!-- Erro/Sucesso -->
        <div v-if="actionMsg" :class="['mt-4 p-3 rounded-xl text-sm', actionError ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700']">
          {{ actionMsg }}
        </div>
      </template>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getAppointment, confirmAppointment, completeAppointment, cancelAppointment, reviewAppointment } from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const route = useRoute()
const appointment = ref(null)
const loading = ref(true)
const actionLoading = ref(false)
const actionMsg = ref('')
const actionError = ref(false)
const showConfirmModal = ref(false)
const showCompleteModal = ref(false)
const showCancelModal = ref(false)
const cancelReason = ref('')
const cancelReasonTouched = ref(false)

const showFeedbackModal = ref(false)
const feedbackTitle = ref('')
const feedbackMessage = ref('')

const reviewRating = ref(0)
const reviewComment = ref('')
const reviewSubmitting = ref(false)
const addressCopied = ref(false)

const steps = [
  { key: 'pending', label: 'Solicitado', icon: 'mdi-clock-outline' },
  { key: 'confirmed', label: 'Confirmado', icon: 'mdi-check' },
  { key: 'in_progress', label: 'Em andamento', icon: 'mdi-tools' },
  { key: 'awaiting_payment', label: 'Aguardando pagamento', icon: 'mdi-cash' },
  { key: 'completed', label: 'Concluído', icon: 'mdi-check-all' },
]

const currentUser = computed(() => {
  try { return JSON.parse(localStorage.getItem('userData'))?.user } catch { return null }
})

const isProvider = computed(() => appointment.value && currentUser.value?.id === appointment.value.provider_id)

const currentStepIndex = computed(() => {
  if (!appointment.value) return 0
  return steps.findIndex(s => s.key === appointment.value.status)
})

const canReview = computed(() => {
  if (!appointment.value || !currentUser.value) return false
  if (isProvider.value && appointment.value.provider_reviewed_contractor) return false
  if (!isProvider.value && appointment.value.contractor_reviewed_provider) return false
  if (appointment.value.review_expires_at && new Date() > new Date(appointment.value.review_expires_at)) return false
  return true
})

const statusLabel = computed(() => {
  const map = {
    pending: 'Aguardando Confirmação',
    confirmed: 'Confirmado',
    in_progress: 'Em Andamento',
    awaiting_payment: 'Aguardando Pagamento',
    completed: 'Concluído',
    cancelled: 'Cancelado'
  }
  return map[appointment.value?.status] || appointment.value?.status
})

const statusClass = computed(() => {
  const map = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-indigo-100 text-indigo-700',
    awaiting_payment: 'bg-orange-100 text-orange-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700'
  }
  return map[appointment.value?.status] || 'bg-gray-100 text-gray-600'
})

const scheduledAtDisplay = computed(() => {
  if (!appointment.value?.scheduled_at) return ''
  return new Date(appointment.value.scheduled_at).toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'short' })
})

const contractorAddress = computed(() => {
  if (!appointment.value?.contractor_address) return null
  if (typeof appointment.value.contractor_address === 'string') {
    try { return JSON.parse(appointment.value.contractor_address) } catch { return null }
  }
  return appointment.value.contractor_address
})

const isCancelReasonValid = computed(() => String(cancelReason.value || '').trim().length >= 3)

function formatPrice (p) {
  return Number(p || 0).toFixed(2).replace('.', ',')
}

function copyAddress () {
  if (!contractorAddress.value) return
  const a = contractorAddress.value
  const parts = [
    `${a.address}, ${a.number}`,
    a.complement || null,
    a.neighborhood,
    `${a.city}/${a.state}`,
    a.zip_code || null
  ].filter(Boolean)
  navigator.clipboard.writeText(parts.join(' - ')).then(() => {
    addressCopied.value = true
    setTimeout(() => { addressCopied.value = false }, 2000)
  })
}

async function action (type) {
  if (type === 'cancel') {
    cancelReasonTouched.value = true
    if (!isCancelReasonValid.value) {
      actionError.value = true
      actionMsg.value = 'Informe o motivo do cancelamento com no mínimo 3 caracteres.'
      return
    }
  }

  actionMsg.value = ''
  actionError.value = false
  actionLoading.value = true

  try {
    if (type === 'confirm') {
      await confirmAppointment(appointment.value.id)
      actionMsg.value = 'Agendamento confirmado com sucesso!'
      feedbackTitle.value = 'Agendamento confirmado'
      feedbackMessage.value = 'Seu agendamento foi confirmado com sucesso. Você receberá um e-mail com os detalhes.'
      showFeedbackModal.value = true
    } else if (type === 'complete') {
      await completeAppointment(appointment.value.id)
      actionMsg.value = 'Status atualizado com sucesso!'
    } else if (type === 'cancel') {
      await cancelAppointment(appointment.value.id, { reason: String(cancelReason.value || '').trim() })
      actionMsg.value = 'Agendamento cancelado.'
      showCancelModal.value = false
      cancelReason.value = ''
      cancelReasonTouched.value = false
      feedbackTitle.value = 'Agendamento cancelado'
      feedbackMessage.value = 'Seu agendamento foi cancelado. Um e-mail foi enviado informando o cancelamento.'
      showFeedbackModal.value = true
    }
    try {
      await reload()
    } catch (reloadErr) {
      // Evita quebrar o feedback da ação se o reload falhar
      console.error('Erro ao recarregar agendamento:', reloadErr)
    }
  } catch (err) {
    actionError.value = true
    actionMsg.value = err?.response?.data?.error || 'Erro ao processar ação.'
  } finally {
    actionLoading.value = false
  }
}

async function submitReview () {
  if (!reviewRating.value) return
  reviewSubmitting.value = true
  try {
    await reviewAppointment(appointment.value.id, { rating: reviewRating.value, comment: reviewComment.value })
    actionMsg.value = 'Avaliação enviada com sucesso!'
    await reload()
  } catch (err) {
    actionError.value = true
    actionMsg.value = err?.response?.data?.error || 'Erro ao enviar avaliação.'
  } finally {
    reviewSubmitting.value = false
  }
}

async function reload () {
  const { data } = await getAppointment(route.params.id)
  appointment.value = data.data
}

onMounted(async () => {
  try {
    await reload()
    if (route.query.booked === '1') {
      feedbackTitle.value = 'Agendamento solicitado!'
      feedbackMessage.value = 'Seu agendamento foi enviado com sucesso. Aguarde a confirmação do prestador.'
      showFeedbackModal.value = true
    }
  } catch {
    appointment.value = null
  } finally {
    loading.value = false
  }
})
</script>
